// Join Activity Page
// User interface for joining activities via shared links with email verification

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  AlertCircle, 
  Users, 
  Calendar, 
  MapPin, 
  Activity,
  Clock,
  X,
  Shield
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRealtime } from '../contexts/RealtimeContext';
import { 
  listenToActivityRegistration, 
  listenToActivityParticipants,
  isRegistrationOpen,
  isRegistrationExpired
} from '../firebase/activityRegistrations';
import toast from 'react-hot-toast';

const JoinActivity = () => {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useRealtime();
  const [registration, setRegistration] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if user is authorized to join
  useEffect(() => {
    if (!registrationId || !currentUser) {
      setIsChecking(false);
      return;
    }

    const checkAuthorization = async () => {
      try {
        // Load participants to check if current user is registered
        const unsubscribe = listenToActivityParticipants(registrationId, (result) => {
          if (result.success) {
            setParticipants(result.data);
            
            // Check if current user is in the participants list
            const userParticipant = result.data.find(
              participant => participant.userId === currentUser.uid
            );
            
            if (userParticipant) {
              setIsAuthorized(true);
            } else {
              setIsAuthorized(false);
            }
            setIsChecking(false);
          } else {
            setIsAuthorized(false);
            setIsChecking(false);
          }
        });

        return unsubscribe;
      } catch (error) {
        setIsAuthorized(false);
        setIsChecking(false);
      }
    };

    checkAuthorization();
  }, [registrationId, currentUser]);

  // Load registration data
  useEffect(() => {
    if (!registrationId) return;

    const unsubscribe = listenToActivityRegistration(registrationId, (result) => {
      if (result.success) {
        setRegistration(result.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, [registrationId]);

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (isLoading || isChecking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Activity Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            The requested activity could not be found or may have been removed.
          </p>
          <button
            onClick={() => navigate('/hobbies')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Go to Hobbies Hub
          </button>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-orange-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Login Required
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You must be logged in to access this activity.
          </p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Login
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <X className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You are not registered for this activity. Only registered participants can access this link.
          </p>
          <div className="space-x-4">
            <button
              onClick={() => navigate('/hobbies')}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go to Hobbies Hub
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isOpen = isRegistrationOpen(registration);
  const isExpired = isRegistrationExpired(registration);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 dark:from-gray-900 dark:via-green-900 dark:to-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 p-6 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2 flex items-center">
                  <Activity className="h-8 w-8 mr-3" />
                  {registration.title}
                </h1>
                <p className="text-green-100 text-lg">{registration.description}</p>
              </div>
              
              <div className="text-right">
                <div className="flex items-center text-green-100 mb-2">
                  <Users className="h-5 w-5 mr-2" />
                  <span className="text-sm">{participants.length} participants</span>
                </div>
                
                <div className="flex items-center text-green-100">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  <span className="text-sm font-medium">You're Registered!</span>
                </div>
              </div>
            </div>
          </div>

          {/* Activity Details */}
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <Calendar className="h-6 w-6 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Activity Date</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {formatDate(registration.activityDate)}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <MapPin className="h-6 w-6 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Location</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {registration.location}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <Activity className="h-6 w-6 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Activity Type</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {registration.activityType}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <Users className="h-6 w-6 text-gray-500 mr-3" />
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Capacity</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {registration.maxParticipants} participants
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Requirements */}
            {registration.requirements && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Requirements
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {registration.requirements}
                </p>
              </div>
            )}

            {/* Joining Instructions */}
            {registration.joiningDescription && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  Joining Instructions
                </h3>
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-blue-800 dark:text-blue-200 whitespace-pre-line">
                    {registration.joiningDescription}
                  </p>
                </div>
              </div>
            )}

            {/* Status */}
            <div className="mb-6">
              <div className={`p-4 rounded-lg ${
                isOpen 
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : isExpired
                  ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800'
                  : 'bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600'
              }`}>
                <div className="flex items-center">
                  {isOpen ? (
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                  ) : isExpired ? (
                    <AlertCircle className="h-5 w-5 text-orange-500 mr-2" />
                  ) : (
                    <Clock className="h-5 w-5 text-gray-500 mr-2" />
                  )}
                  <span className={`font-medium ${
                    isOpen 
                      ? 'text-green-800 dark:text-green-200'
                      : isExpired
                      ? 'text-orange-800 dark:text-orange-200'
                      : 'text-gray-800 dark:text-gray-200'
                  }`}>
                    {isOpen ? 'Registration Open' : isExpired ? 'Registration Closed' : 'Registration Not Started'}
                  </span>
                </div>
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center p-6 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-green-800 dark:text-green-200 mb-2">
                Welcome to the Activity!
              </h3>
              <p className="text-green-600 dark:text-green-400 mb-4">
                You have successfully joined this activity. We'll contact you with further details.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <button
                  onClick={() => navigate('/hobbies')}
                  className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  View More Activities
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default JoinActivity;
